/* eslint-disable */
import { useEffect, useState } from "react";
import { csvParse } from "d3-dsv";

/* =====================================================================
 * SOURCE ADAPTER — SNAPSHOT-FIRST PUBLISHED DATA
 * ---------------------------------------------------------------------
 * Rankings use one compact current JSON; selected-title history/details are
 * fetched separately. loadXboxSourceDataset is the shared offline/export
 * normalizer for the three canonical source files, never a browser fallback.
 * ===================================================================== */
const XBOX_DATA_BASE_URL = (
  process.env.REACT_APP_XBOX_DATA_BASE_URL || "https://mindgamedataapi.s3.us-east-1.amazonaws.com/Xbox"
).replace(/\/+$/, "");

async function fetchText(url) {
  const filename = url.split("/").pop();
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.text();
  } catch (error) {
    throw new Error(`Could not load ${filename}: ${error?.message || "network error"}`);
  }
}

export async function loadXboxSourceDataset(texts) {
  // Accept either raw CSV/JSON strings (browser/tests) or pre-parsed
  // rows/objects (Lambda builder streams large files to avoid Node's string
  // length limit).
  const current = typeof texts.current === "string" ? csvParse(texts.current) : texts.current;
  const history = typeof texts.history === "string" ? csvParse(texts.history) : texts.history;
  const metadata = typeof texts.metadata === "string" ? JSON.parse(texts.metadata) : texts.metadata;
  for (const rows of [current, history]) {
    const required = ["snapshot_utc", "title_id", "type", "active_users", "rank_all", "rank_games", "rank_apps", "share_all", "share_games", "share_apps"];
    // d3-dsv exposes columns on the array; papaparse (streaming) does not.
    const columns = rows.columns || (rows.length ? Object.keys(rows[0]) : []);
    if (!rows.length || required.some(column => !columns.includes(column))) throw new Error("Invalid Xbox source schema");
    for (const row of rows) {
      if (!/^[0-9]+$/.test(row.title_id) || !Number.isFinite(Date.parse(row.snapshot_utc)) ||
          !row.active_users.trim() || !Number.isFinite(Number(row.active_users)) || Number(row.active_users) < 0) {
        throw new Error("Invalid Xbox source observation");
      }
    }
  }
  const currentDates = new Set(current.map(row => row.snapshot_utc));
  if (currentDates.size !== 1 || Date.parse(current[0].snapshot_utc) > Date.now()) throw new Error("Invalid Xbox current snapshot timestamp");
  const historyLatest = history.reduce((latest, row) => Math.max(latest, Date.parse(row.snapshot_utc)), -Infinity);
  if (historyLatest !== Date.parse(current[0].snapshot_utc)) throw new Error("Xbox history/current uploads have not converged");
  return normalizeDataset({
    current, history, metadata,
    completedSnapshotUtc: [...new Set([...current, ...history].map(row => row.snapshot_utc))],
  });
}

async function fetchJson(url) {
  return JSON.parse(await fetchText(url));
}

function snapshotObjectUrl(key) {
  if (typeof key !== "string" || !/^dashboard\/[a-f0-9]{64}\/(?:current\.json|titles\/[0-9]+\.json)$/.test(key)) {
    throw new Error("Invalid Xbox snapshot object path");
  }
  return `${XBOX_DATA_BASE_URL}/${key}`;
}

async function fetchXboxSnapshot() {
  const manifest = await fetchJson(`${XBOX_DATA_BASE_URL}/dashboard/manifest.json`);
  if (manifest.schemaVersion !== 1 || !Number.isFinite(Date.parse(manifest.asOf))) {
    throw new Error("Invalid Xbox snapshot manifest");
  }
  const snapshot = await fetchJson(snapshotObjectUrl(manifest.current));
  if (snapshot.schemaVersion !== 1 || snapshot.asOf !== manifest.asOf ||
      !snapshot.titles || !snapshot.rows || !Array.isArray(snapshot.timeline) || !Array.isArray(snapshot.activityMix) ||
      !["all", "games", "apps"].every(mode => Array.isArray(snapshot.rows[mode]))) {
    throw new Error("Invalid Xbox current snapshot");
  }
  const dataset = {
    titles: new Map(Object.entries(snapshot.titles)),
    firstObservedAt: new Map(Object.entries(snapshot.firstObservedAt)),
    snapshots: snapshot.timeline.map(row => ({ snapshotUtc: row.snapshotUtc, time: row.time })),
    _cache: new Map(), compact: snapshot, manifest,
    histories: new Map(), historyRequests: new Map(),
  };
  dataset.latest = dataset.snapshots.find(row => row.snapshotUtc === snapshot.asOf);
  if (!dataset.latest) throw new Error("Xbox current timestamp is missing from its timeline");
  return dataset;
}

export function loadXboxTitleHistory(dataset, titleId) {
  const id = String(titleId);
  if (!dataset.compact) return Promise.resolve();
  if (!dataset.titles.has(id) || !/^[0-9]+$/.test(id)) return Promise.reject(new Error("Unknown Xbox title"));
  if (dataset.histories.has(id)) return Promise.resolve(dataset.histories.get(id));
  if (!dataset.historyRequests.has(id)) {
    const prefix = dataset.manifest.current.replace(/current\.json$/, "");
    const request = fetchJson(snapshotObjectUrl(`${prefix}titles/${id}.json`)).then(history => {
      if (history.schemaVersion !== 1 || history.titleId !== id || history.asOf !== dataset.compact.asOf ||
          !["all", "games", "apps"].every(mode => Array.isArray(history.modes?.[mode]) &&
            history.modes[mode].length === dataset.snapshots.length &&
            history.modes[mode].every((row, index) => row.time === dataset.snapshots[index].time))) {
        throw new Error(`Invalid Xbox history for ${id}`);
      }
      dataset.histories.set(id, history);
      return history;
    }).catch(error => {
      dataset.historyRequests.delete(id);
      throw error;
    });
    dataset.historyRequests.set(id, request);
  }
  return dataset.historyRequests.get(id);
}

export function useXboxTitleHistories(dataset, titleIds) {
  const [state, setState] = useState({ version: 0, error: null, loading: false });
  const [attempt, setAttempt] = useState(0);
  const key = [...new Set(titleIds)].sort().join(",");
  useEffect(() => {
    if (!dataset?.compact || !key) return;
    let alive = true;
    const ids = key.split(",");
    setState(previous => ({ ...previous, loading: ids.some(id => !dataset.histories.has(id)), error: null }));
    Promise.allSettled(ids.map(id => loadXboxTitleHistory(dataset, id))).then(results => {
      if (!alive) return;
      const errors = results.filter(result => result.status === "rejected").map(result => result.reason.message);
      setState(previous => ({ version: previous.version + 1, error: errors.length ? errors.join("; ") : null, loading: false }));
    });
    return () => { alive = false; };
  }, [dataset, key, attempt]);
  return { ...state, retry: () => setAttempt(previous => previous + 1) };
}

/* =====================================================================
 * NORMALIZATION
 * ===================================================================== */
export const XBOX_MODES = { GAMES: "games", APPS: "apps", ALL: "all" };

// PROVISIONAL FRONTEND CALIBRATION
// Temporary model for visual evaluation.
// Replace with server-side calibrated Xbox population estimates later.
export const XBOX_CCU_CALIBRATION = [
  { startHour: 0, endHour: 6, playersPerSharePoint: 25000 },
  { startHour: 6, endHour: 12, playersPerSharePoint: 30000 },
  { startHour: 12, endHour: 17, playersPerSharePoint: 37500 },
  { startHour: 17, endHour: 22, playersPerSharePoint: 42500 },
  { startHour: 22, endHour: 24, playersPerSharePoint: 35000 },
];

const PACIFIC_HOUR_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  hour: "numeric",
  hourCycle: "h23",
});

// Each mode reads the collector's own rank/share columns; nothing is re-derived in the browser.
const MODE_FIELDS = {
  games: { type: "Game", rank: "rankGames", share: "shareGames" },
  apps: { type: "Application", rank: "rankApps", share: "shareApps" },
  all: { type: null, rank: "rankAll", share: "shareAll" },
};

const numOrNull = (v) => {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function getPacificHour(snapshotTimestamp) {
  const time = typeof snapshotTimestamp === "number" ? snapshotTimestamp : Date.parse(snapshotTimestamp);
  if (!Number.isFinite(time)) return null;
  const hour = Number(PACIFIC_HOUR_FORMATTER.formatToParts(new Date(time)).find((part) => part.type === "hour")?.value);
  return Number.isInteger(hour) && hour >= 0 && hour < 24 ? hour : null;
}

export function getXboxGameShareMultiplier(snapshotTimestamp) {
  const hour = getPacificHour(snapshotTimestamp);
  if (hour == null) return null;
  return XBOX_CCU_CALIBRATION.find((bucket) => hour >= bucket.startHour && hour < bucket.endHour)?.playersPerSharePoint ?? null;
}

export const XBOX_CONSOLES = ["Xbox Series X|S", "Xbox One", "Xbox 360"];

const DEVICE_LABELS = {
  scarlett: "Xbox Series X|S",
  xboxseries: "Xbox Series X|S",
  xboxone: "Xbox One",
  xbox360: "Xbox 360",
  pc: "PC",
  mobile: "Mobile",
};

const deviceLabel = (raw) => DEVICE_LABELS[String(raw || "").trim().toLowerCase()] || String(raw || "").trim();

// Upstream values arrive as arrays, "a | b" strings, or Python list reprs ("['a', 'b']").
function parseList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  const s = String(value ?? "").trim();
  if (!s || s === "[]") return [];
  if (s.startsWith("[")) {
    const out = [];
    s.replace(/'([^']*)'|"([^"]*)"/g, (_, a, b) => {
      const v = (a ?? b ?? "").trim();
      if (v) out.push(v);
      return "";
    });
    return out;
  }
  return s.split("|").map((v) => v.trim()).filter(Boolean);
}

// Microsoft mixes "Action & adventure" / "Action & Adventure".
const canonicalGenre = (g) =>
  g
    .split(" ")
    .map((w) => (w && w !== "&" ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");

const toHttps = (url) => (url ? String(url).trim().replace(/^http:\/\//i, "https://") : "");

const uniqueUrls = (...urls) => Array.from(new Set(urls.map(toHttps).filter(Boolean)));

function parseObservedDevices(raw) {
  const out = {};
  String(raw || "")
    .split("|")
    .forEach((part) => {
      const [k, v] = part.split(":");
      const n = Number(v);
      if (!k || !Number.isFinite(n)) return;
      const label = deviceLabel(k);
      out[label] = (out[label] || 0) + n;
    });
  return out;
}

// The snapshot CSV's `type` defines the game/app populations behind rank_* and share_*.
function resolveType(csvType, metaType) {
  if (csvType === "Game" || csvType === "Application") return csvType;
  if (csvType) return "Unknown";
  if (metaType === "Game" || metaType === "Application") return metaType;
  return "Unknown";
}

function normalizeTitle(titleId, meta, csvRow) {
  const m = meta || {};
  const genres = Array.from(new Set(parseList(m.genres ?? csvRow?.genres).map(canonicalGenre)));
  const supportedDevices = Array.from(new Set(parseList(m.devices ?? csvRow?.devices).map(deviceLabel)));
  return {
    titleId,
    name: m.name || csvRow?.name || csvRow?.presence_name || `Title ${titleId}`,
    type: resolveType(csvRow?.type, m.type),
    publisher: (m.publisher || csvRow?.publisher || "").trim(),
    developer: (m.developer || csvRow?.developer || "").trim(),
    genres,
    supportedDevices,
    releaseDate: m.release_date || csvRow?.release_date || "",
    gamePass: typeof m.game_pass === "boolean" ? m.game_pass : null,
    shortDescription: (m.short_description || csvRow?.short_description || "").trim(),
    description: (m.description || csvRow?.description || "").trim(),
    mainArt: uniqueUrls(m.main_art_url, m.box_art_url, m.poster_url, m.display_image),
    backgroundArt: uniqueUrls(m.background_art_url, m.super_hero_url, m.titled_hero_url, m.hero_url),
    hasMetadata: !!meta && meta.metadata_status === "ok",
  };
}

function normalizeDataset({ current, history, metadata, completedSnapshotUtc = [] }) {
  const meta = metadata || {};
  const titles = new Map();
  const titleTimes = new Map();
  const firstObservedAt = new Map();
  const completed = new Set(completedSnapshotUtc);
  const snapshotsByUtc = new Map();

  const ingest = (row, overwrite) => {
    const titleId = String(row.title_id || "").trim();
    const utc = String(row.snapshot_utc || "").trim();
    if (!titleId || !utc) return;

    // Current-snapshot rows win so title fields reflect the latest classification.
    const time = Date.parse(utc);
    if (Number.isFinite(time)) firstObservedAt.set(titleId, Math.min(firstObservedAt.get(titleId) ?? time, time));
    if (!titles.has(titleId) || overwrite || time >= titleTimes.get(titleId)) {
      titles.set(titleId, normalizeTitle(titleId, meta[titleId], row));
      titleTimes.set(titleId, time);
    }

    let snap = snapshotsByUtc.get(utc);
    if (!snap) {
      snap = { snapshotUtc: utc, time: Date.parse(utc), observations: new Map(), fromCurrent: false, isComplete: completed.has(utc) };
      snapshotsByUtc.set(utc, snap);
    }
    if (overwrite && !snap.fromCurrent) {
      snap.observations = new Map();
      snap.fromCurrent = true;
    }
    snap.observations.set(titleId, {
      titleId,
      type: resolveType(row.type, meta[titleId]?.type),
      activeUsers: Number(row.active_users) || 0,
      rankAll: numOrNull(row.rank_all),
      rankGames: numOrNull(row.rank_games),
      rankApps: numOrNull(row.rank_apps),
      shareAll: numOrNull(row.share_all),
      shareGames: numOrNull(row.share_games),
      shareApps: numOrNull(row.share_apps),
      observedDevices: parseObservedDevices(row.observed_devices),
    });
  };

  (history || []).forEach((r) => ingest(r, false));
  // The current snapshot file is authoritative for its timestamp.
  (current || []).forEach((r) => ingest(r, true));

  const snapshots = Array.from(snapshotsByUtc.values())
    .filter((s) => Number.isFinite(s.time))
    .sort((a, b) => a.time - b.time);

  const latestUtc = current?.[0]?.snapshot_utc || snapshots[snapshots.length - 1]?.snapshotUtc || null;

  return {
    titles,
    firstObservedAt,
    snapshots,
    latest: snapshots.find((s) => s.snapshotUtc === latestUtc) || snapshots[snapshots.length - 1] || null,
    _cache: new Map(),
  };
}

let datasetPromise = null;

/** Loads + normalizes once per session; every Xbox component shares the result. */
export function loadXboxDataset() {
  if (!datasetPromise) {
    datasetPromise = fetchXboxSnapshot()
      .catch((err) => {
        datasetPromise = null;
        throw err;
      });
  }
  return datasetPromise;
}

/** Drops the cached dataset so the next loadXboxDataset() refetches. */
function invalidateXboxDataset() {
  datasetPromise = null;
}

/** Test-only hook to reset the module-level dataset cache between tests. */
export function __resetXboxDatasetCacheForTests() {
  datasetPromise = null;
}

/** Fetches only the manifest and returns its asOf, or null on any failure. */
async function fetchManifestAsOf() {
  try {
    const manifest = await fetchJson(`${XBOX_DATA_BASE_URL}/dashboard/manifest.json`);
    return manifest && typeof manifest.asOf === "string" ? manifest.asOf : null;
  } catch {
    return null;
  }
}

const XBOX_POLL_INTERVAL_MS = 10 * 60 * 1000; // check for a new hourly snapshot

export function useXboxDataset() {
  const [state, setState] = useState({ dataset: null, loading: true, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setState({ dataset: null, loading: true, error: null });
    loadXboxDataset()
      .then((dataset) => alive && setState({ dataset, loading: false, error: null }))
      .catch((error) => alive && setState({ dataset: null, loading: false, error: error?.message || String(error) }));
    return () => {
      alive = false;
    };
  }, [attempt]);

  // Poll the manifest so a newly published hourly snapshot is picked up
  // automatically. Only reloads when the manifest's asOf actually changes.
  const currentAsOf = state.dataset?.manifest?.asOf || null;
  useEffect(() => {
    if (!currentAsOf) return undefined;
    const timer = setInterval(async () => {
      const latestAsOf = await fetchManifestAsOf();
      if (latestAsOf && latestAsOf !== currentAsOf) {
        invalidateXboxDataset();
        setAttempt((previous) => previous + 1);
      }
    }, XBOX_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [currentAsOf]);

  return { ...state, retry: () => setAttempt((previous) => previous + 1) };
}

/* =====================================================================
 * METRICS — rank/share come straight from the source columns for the mode
 * ===================================================================== */
const inMode = (obs, mode) => {
  const f = MODE_FIELDS[mode] || MODE_FIELDS.games;
  return !f.type || obs.type === f.type;
};

function memo(dataset, key, fn) {
  if (!dataset._cache.has(key)) dataset._cache.set(key, fn());
  return dataset._cache.get(key);
}

/** Derive calibration denominators from this exact snapshot, never from the current snapshot or browser clock. */
export function getSnapshotActivityStats(dataset, snapshot) {
  return memo(dataset, `activity-stats:${snapshot.snapshotUtc}`, () => {
    let totalGameActivity = 0;
    let totalAllActivity = 0;
    snapshot.observations.forEach((obs) => {
      totalAllActivity += obs.activeUsers;
      if (obs.type === "Game") totalGameActivity += obs.activeUsers;
    });
    const gameFraction = totalAllActivity > 0 ? totalGameActivity / totalAllActivity : 0;
    const gamePlayersPerSharePoint = getXboxGameShareMultiplier(snapshot.time);
    const allActivityPlayersPerSharePoint = gameFraction > 0 && gamePlayersPerSharePoint != null
      ? gamePlayersPerSharePoint / gameFraction
      : null;
    const estimatedGamingPopulation = gamePlayersPerSharePoint == null ? null : gamePlayersPerSharePoint * 100;
    const estimatedTotalActivePopulation = allActivityPlayersPerSharePoint == null
      ? null
      : allActivityPlayersPerSharePoint * 100;
    return {
      snapshotUtc: snapshot.snapshotUtc,
      snapshotTime: snapshot.time,
      pacificHour: getPacificHour(snapshot.time),
      gamePlayersPerSharePoint,
      allActivityPlayersPerSharePoint,
      totalGameActivity,
      totalAllActivity,
      gameFraction,
      estimatedGamingPopulation,
      estimatedTotalActivePopulation,
      estimatedAppPopulation: estimatedTotalActivePopulation == null || estimatedGamingPopulation == null
        ? null
        : estimatedTotalActivePopulation - estimatedGamingPopulation,
    };
  });
}

/** Games use share_games; apps use share_all even in the app-only view. */
export function estimateXboxPlayers({ share, mode, snapshotTimestamp, gameFraction }) {
  const gameMultiplier = getXboxGameShareMultiplier(snapshotTimestamp);
  if (share == null || gameMultiplier == null) return null;
  if (mode === XBOX_MODES.GAMES) return share * gameMultiplier;
  if (!(gameFraction > 0)) return null;
  return share * (gameMultiplier / gameFraction);
}

/** Share/rank for every in-mode title in one snapshot. */
export function getSnapshotShares(dataset, snapshot, mode) {
  return memo(dataset, `shares:${snapshot.snapshotUtc}:${mode}`, () => {
    const f = MODE_FIELDS[mode] || MODE_FIELDS.games;
    const entries = [];
    let total = 0;
    snapshot.observations.forEach((obs) => {
      if (!inMode(obs, mode) || obs[f.rank] == null) return;
      total += obs.activeUsers;
      entries.push(obs);
    });
    entries.sort((a, b) => a[f.rank] - b[f.rank]);
    const byId = new Map();
    entries.forEach((obs) =>
      byId.set(obs.titleId, {
        rank: obs[f.rank],
        activeUsers: obs.activeUsers,
        share: obs[f.share] ?? 0,
      })
    );
    return { total, titleCount: entries.length, byId };
  });
}

/** Rows for the latest snapshot, with change vs the previous snapshot when one exists. */
export function getModeRows(dataset, mode) {
  if (dataset.compact) return memo(dataset, `rows:${mode}`, () =>
    dataset.compact.rows[mode].map(row => ({
      ...row, title: dataset.titles.get(row.titleId), shareHistory: [],
    })));
  return memo(dataset, `rows:${mode}`, () => {
    const { latest, snapshots } = dataset;
    if (!latest) return [];
    const idx = snapshots.indexOf(latest);
    const prev = idx > 0 ? snapshots[idx - 1] : null;
    const cur = getSnapshotShares(dataset, latest, mode);
    const prevShares = prev ? getSnapshotShares(dataset, prev, mode) : null;
    const currentStats = getSnapshotActivityStats(dataset, latest);

    const rows = [];
    const bottomRank = Array.from(cur.byId.values()).filter((entry) => entry.activeUsers > 0).length + 1;
    const appendRow = (entry, titleId, isInactive) => {
      const p = prevShares?.byId.get(titleId) || null;
      const historicalObservedDevices = {};
      snapshots.forEach((snapshot) => {
        Object.entries(snapshot.observations.get(titleId)?.observedDevices || {}).forEach(([device, count]) => {
          historicalObservedDevices[device] = (historicalObservedDevices[device] || 0) + count;
        });
      });
      rows.push({
        titleId,
        title: dataset.titles.get(titleId),
        isInactive,
        rank: entry.rank,
        share: entry.share,
        activeUsers: entry.activeUsers,
        estimatedPlayers: isInactive ? 0 : estimateXboxPlayers({
          share: mode === XBOX_MODES.GAMES ? entry.share : latest.observations.get(titleId)?.shareAll,
          mode,
          snapshotTimestamp: latest.time,
          gameFraction: currentStats.gameFraction,
        }),
        observedDevices: isInactive ? {} : latest.observations.get(titleId)?.observedDevices || {},
        historicalObservedDevices,
        prevShare: p ? p.share : null,
        prevRank: p ? p.rank : null,
        change: p ? entry.share - p.share : null,
        isNew: !isInactive && !!prevShares && !p,
        shareHistory: snapshots.map((snapshot) => ({ time: snapshot.time, ...getHistoricalTitleActivity(dataset, snapshot, mode, titleId) })),
      });
    };
    cur.byId.forEach((entry, titleId) => {
      if (entry.activeUsers > 0) appendRow(entry, titleId, false);
    });
    const inactiveTitles = Array.from(dataset.titles.values())
      .filter((title) => inMode(title, mode) && !(cur.byId.get(title.titleId)?.activeUsers > 0))
      .sort((first, second) => first.name.localeCompare(second.name) || first.titleId.localeCompare(second.titleId));
    inactiveTitles.forEach((title) => appendRow({ rank: bottomRank, share: 0, activeUsers: 0 }, title.titleId, true));
    return rows;
  });
}

export function getModeTotals(dataset, mode) {
  if (dataset?.compact) return dataset.compact.totals[mode];
  if (!dataset?.latest) return { total: 0, titleCount: 0 };
  const { total, titleCount } = getSnapshotShares(dataset, dataset.latest, mode);
  return { total, titleCount };
}

/** Games vs Applications vs unclassified, as a share of ALL observed activity, per snapshot. */
export function getActivityMix(dataset) {
  if (dataset.compact) return dataset.compact.activityMix;
  return memo(dataset, "mix", () =>
    dataset.snapshots.map((snap) => {
      const users = { Game: 0, Application: 0, Unknown: 0 };
      const counts = { Game: 0, Application: 0, Unknown: 0 };
      const gamePassUsers = { yes: 0, no: 0, unknown: 0 };
      snap.observations.forEach((obs) => {
        const type = obs.type || "Unknown";
        users[type] += obs.activeUsers;
        counts[type] += 1;
        if (type === "Game") {
          const pass = dataset.titles.get(obs.titleId)?.gamePass;
          const key = pass === true ? "yes" : pass === false ? "no" : "unknown";
          gamePassUsers[key] += obs.activeUsers;
        }
      });
      const total = users.Game + users.Application + users.Unknown;
      const activityStats = getSnapshotActivityStats(dataset, snap);
      const pct = (v) => (total > 0 ? (v / total) * 100 : 0);
      return {
        snapshotUtc: snap.snapshotUtc,
        time: snap.time,
        total,
        users,
        counts,
        pct: { Game: pct(users.Game), Application: pct(users.Application), Unknown: pct(users.Unknown) },
        ...activityStats,
        activitySegments: [
          { key: "games", label: "Games", observed: users.Game, estimated: activityStats.estimatedGamingPopulation },
          { key: "apps", label: "Apps + other", observed: users.Application + users.Unknown, estimated: activityStats.estimatedAppPopulation },
        ],
        gamePassSegments: [
          {
            key: "gamePass",
            label: "Game Pass",
            observed: gamePassUsers.yes,
            estimated: activityStats.estimatedGamingPopulation != null && users.Game > 0
              ? activityStats.estimatedGamingPopulation * gamePassUsers.yes / users.Game
              : null,
          },
          {
            key: "otherGames",
            label: "Outside Game Pass",
            observed: gamePassUsers.no + gamePassUsers.unknown,
            estimated: activityStats.estimatedGamingPopulation != null && users.Game > 0
              ? activityStats.estimatedGamingPopulation * (gamePassUsers.no + gamePassUsers.unknown) / users.Game
              : null,
          },
          {
            key: "appsOther",
            label: "Apps / other",
            observed: users.Application + users.Unknown,
            estimated: activityStats.estimatedAppPopulation,
          },
        ],
      };
    })
  );
}

/** Builds current-snapshot pie aggregates from the rows that survive page-level filters. */
export function getFilteredActivityMix(rows) {
  const users = { Game: 0, Application: 0, Unknown: 0 };
  const counts = { Game: 0, Application: 0, Unknown: 0 };
  const gamePassUsers = { yes: 0, no: 0 };
  const gamePassEstimated = { yes: 0, no: 0 };
  let estimatedTotal = 0;
  let estimatedGaming = 0;
  let estimatedAppsOther = 0;

  (rows || []).forEach((row) => {
    if (row.isInactive) return;
    const type = row.title?.type || "Unknown";
    users[type] += row.activeUsers;
    counts[type] += 1;
    estimatedTotal += row.estimatedPlayers || 0;
    if (type === "Game") {
      estimatedGaming += row.estimatedPlayers || 0;
      const passStatus = row.title.gamePass === true ? "yes" : "no";
      gamePassUsers[passStatus] += row.activeUsers;
      gamePassEstimated[passStatus] += row.estimatedPlayers || 0;
    } else {
      estimatedAppsOther += row.estimatedPlayers || 0;
    }
  });

  return {
    users,
    counts,
    totalObserved: users.Game + users.Application + users.Unknown,
    estimatedTotal,
    estimatedGaming,
    estimatedAppsOther,
    activitySegments: [
      { key: "games", label: "Games", observed: users.Game, estimated: estimatedGaming },
      { key: "apps", label: "Apps", observed: users.Application + users.Unknown, estimated: estimatedAppsOther },
    ],
    gamePassSegments: [
      { key: "gamePass", label: "Game Pass", observed: gamePassUsers.yes, estimated: gamePassEstimated.yes },
      { key: "otherGames", label: "Outside Game Pass", observed: gamePassUsers.no, estimated: gamePassEstimated.no },
      { key: "appsOther", label: "Apps / Other", observed: users.Application + users.Unknown, estimated: estimatedAppsOther },
    ],
  };
}

function getHistoricalTitleActivity(dataset, snapshot, mode, titleId) {
  const entry = getSnapshotShares(dataset, snapshot, mode).byId.get(titleId);
  const observation = snapshot.observations.get(titleId);
  const firstObserved = dataset.firstObservedAt.get(titleId);
  const absentTracked = !observation && snapshot.isComplete && firstObserved != null && snapshot.time >= firstObserved && inMode(dataset.titles.get(titleId), mode);
  const stats = getSnapshotActivityStats(dataset, snapshot);
  return {
    share: entry ? entry.share : absentTracked ? 0 : null,
    rank: entry?.rank ?? null,
    activeUsers: entry ? entry.activeUsers : absentTracked ? 0 : null,
    estimatedPlayers: absentTracked ? 0 : estimateXboxPlayers({
      share: mode === XBOX_MODES.GAMES ? entry?.share : observation?.shareAll,
      mode,
      snapshotTimestamp: snapshot.time,
      gameFraction: stats.gameFraction,
    }),
  };
}

/** Only completed snapshots can establish zero activity after a title was first observed. */
export function getHistorySeries(dataset, mode, titleIds) {
  if (dataset.compact) return dataset.compact.timeline.map((snapshot, index) => {
    const row = { ...snapshot, total: dataset.compact.historyTotals[mode][index] };
    titleIds.forEach(id => {
      const history = dataset.histories.get(id)?.modes[mode]?.[index];
      for (const suffix of ["", "__rank", "__estimated", "__observed"]) {
        row[`${id}${suffix}`] = history?.[`${id}${suffix}`] ?? null;
      }
    });
    return row;
  });
  return dataset.snapshots.map((snap) => {
    const shares = getSnapshotShares(dataset, snap, mode);
    const stats = getSnapshotActivityStats(dataset, snap);
    const row = { time: snap.time, snapshotUtc: snap.snapshotUtc, total: shares.total, ...stats };
    titleIds.forEach((id) => {
      const activity = getHistoricalTitleActivity(dataset, snap, mode, id);
      row[id] = activity.share;
      row[`${id}__rank`] = activity.rank;
      row[`${id}__estimated`] = activity.estimatedPlayers;
      row[`${id}__observed`] = activity.activeUsers;
    });
    return row;
  });
}
